/**
 * The tasks panel, lifted out of the old project dialog.
 *
 * `done` is read from two places on purpose, exactly as the dialog did: the
 * status the operator set AND `completedAt`. The CRM writes the timestamp on
 * some paths and moves the status on others, so a task ticked off by one route
 * and read through the other would show a client an open task that has been
 * finished for weeks.
 *
 * As in the milestones panel, an empty list means the project has no tasks yet -
 * a closed tab never reaches here, because the server strips the payload and
 * leaves the tab out of `detail.tabs`.
 */
import { CheckCircle2, Circle } from "lucide-react";
import { Badge } from "@wa/ui";
import { useT, useI18n } from "../../../../i18n/I18nProvider";
import type { PortalProjectDetail } from "../../../../lib/portal";
import { fmtDate, genericStatusVariant } from "../../../../lib/format";

const TASK_DONE = new Set(["done"]);

export function TasksPanel({ tasks }: { tasks: PortalProjectDetail["tasks"] }) {
  const t = useT();
  const { locale } = useI18n();

  if (tasks.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t("portal.projects.linkedEmpty")}</p>;
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-semibold">{t("portal.projects.tasksTitle")}</h4>
      <ul className="space-y-1.5">
        {tasks.map((task) => {
          const done = TASK_DONE.has(task.status) || !!task.completedAt;
          return (
            <li key={task.id} className="flex flex-wrap items-center gap-2 text-sm">
              {done
                ? <CheckCircle2 className="size-4 shrink-0 text-success" />
                : <Circle className="size-4 shrink-0 text-muted-foreground" />}
              <span className={done ? "text-muted-foreground line-through" : "text-foreground"}>{task.title}</span>
              {task.dueDate && !done && (
                <span className="text-xs text-muted-foreground">{fmtDate(task.dueDate, locale)}</span>
              )}
              <Badge variant={genericStatusVariant(task.status)} className="ml-auto shrink-0">
                {t(`portal.taskStatus.${task.status}`)}
              </Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
